import { useEffect, useRef, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import '@/styles/coffee.css';
import {
  TealMug, ToGoCup, Bean, Sparkle, Heart, Sprig, BrewCup, SuccessMug,
} from '@/components/coffee/CoffeeArt';

const LOCATIONS = ['Charlotte', 'Mooresville', 'Winston-Salem'];

const QUIPS = [
  'Like a good espresso, great tax strategy is all about timing.',
  'Compound interest is just coffee for your cash flow — it brews while you sleep.',
  'Every empire started with one bold cup and a clean balance sheet.',
  'We froth over tidy books almost as much as we love latte art.',
  'Big margins, no bitterness — that’s the KGOB blend.',
];

const BREWS = [
  { art: 'black', stage: 'STARTUP', badge: { bg: '#E2EAF1', fg: '#2B4D72' }, name: 'The Bootstrap Black', blurb: 'Lean, bold, zero fluff — just like your very first set of books.', paired: 'Entity setup & bookkeeping' },
  { art: 'cappuccino', stage: 'GROWTH', badge: { bg: '#F6ECCE', fg: '#9A7A1E' }, name: 'The Cash-Flow Cap', blurb: 'Smooth momentum with a little froth — steady foam, steady margins.', paired: 'Cash-flow & advisory' },
  { art: 'latte', stage: 'SCALE-UP', badge: { bg: '#E2EAF1', fg: '#2B4D72' }, name: 'The Double-Shot Latte', blurb: 'Twice the shots for twice the headcount. Layered, and built to last.', paired: 'Tax strategy & planning' },
  { art: 'affogato', stage: 'EXIT', badge: { bg: '#FBE3DC', fg: '#B45A3C' }, name: 'The Affogato Exit', blurb: 'The sweet scoop on top — the reward you’ve earned at the finish.', paired: 'Succession & exit planning' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const labelS = { display: 'block', fontWeight: 600, fontSize: 13.5, color: '#1B3A5C', marginBottom: 7 };
const inputS = { width: '100%', padding: '13px 15px', border: '1.5px solid #D8D0BD', borderRadius: 11, fontSize: 15, fontFamily: 'inherit', background: '#FCFAF4', color: '#26323F', transition: '.15s' };
const errS = { display: 'block', color: '#C0432F', fontSize: 12.5, marginTop: 5 };

const CONFETTI_COLORS = ['#1AA0B8', '#C9A23F', '#1B3A5C', '#E3C66A'];

function Hero({ animated }) {
  const [qi, setQi] = useState(0);
  useEffect(() => {
    if (!animated) return undefined;
    const t = setInterval(() => setQi((i) => (i + 1) % QUIPS.length), 3800);
    return () => clearInterval(t);
  }, [animated]);

  return (
    <section className="kg-hero" style={{ position: 'relative', overflow: 'hidden', padding: '54px 24px 30px', textAlign: 'center', background: '#F7F0E1' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <span style={{ '--r': '-18deg', position: 'absolute', left: '6%', top: '30%', animation: 'kg-bean 5.5s ease-in-out infinite' }}><Bean w={34} /></span>
        <span style={{ '--r': '14deg', position: 'absolute', right: '8%', top: '18%', animation: 'kg-bean 6.4s ease-in-out infinite .6s' }}><Bean w={30} /></span>
        <span style={{ '--r': '25deg', position: 'absolute', right: '14%', top: '62%', animation: 'kg-bean 5.9s ease-in-out infinite 1.1s' }}><Bean w={26} /></span>
        <span style={{ position: 'absolute', left: '18%', top: '16%', animation: 'kg-twinkle 2.6s ease-in-out infinite' }}><Sparkle size={22} color="#1AA0B8" /></span>
        <span style={{ position: 'absolute', right: '22%', top: '40%', animation: 'kg-twinkle 3.1s ease-in-out infinite .5s' }}><Sparkle size={16} color="#C9A23F" /></span>
        <span style={{ position: 'absolute', left: '30%', top: '60%', animation: 'kg-twinkle 2.9s ease-in-out infinite 1s' }}><Sparkle size={14} color="#1AA0B8" /></span>
        <span style={{ position: 'absolute', left: '78%', top: '8%', animation: 'kg-twinkle 3.4s ease-in-out infinite .8s' }}><Sparkle size={18} color="#C9A23F" /></span>
        <span style={{ '--r': '-12deg', position: 'absolute', left: '11%', top: '70%', animation: 'kg-bob 4.2s ease-in-out infinite' }}><Heart size={26} color="#1AA0B8" opacity={0.85} /></span>
        <span style={{ '--r': '10deg', position: 'absolute', right: '6%', top: '48%', animation: 'kg-bob 3.8s ease-in-out infinite .4s' }}><Heart size={20} color="#C9A23F" opacity={0.75} /></span>
        <span style={{ position: 'absolute', left: '4%', top: '8%', animation: 'kg-drift 6s ease-in-out infinite' }}><Sprig size={40} /></span>
        <div className="kg-hero-mug-left" style={{ position: 'absolute', left: '5%', bottom: 0, animation: 'kg-sip 5.5s ease-in-out infinite', transformOrigin: 'bottom center' }}><TealMug size={130} showSteam /></div>
        <div className="kg-hero-mug-right" style={{ position: 'absolute', right: '5%', bottom: 2, animation: 'kg-sway 6.5s ease-in-out infinite', transformOrigin: 'bottom center' }}><ToGoCup size={92} /></div>
      </div>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 760, margin: '0 auto' }}>
        <div style={{ fontFamily: "'Kaushan Script', cursive", color: '#1AA0B8', fontSize: 38, lineHeight: 1, marginBottom: 6 }}>Welcome!</div>
        <h1 className="kg-h1" style={{ fontWeight: 800, fontSize: 'clamp(34px,5.2vw,58px)', color: '#1B3A5C', lineHeight: 1.04, margin: '0 0 18px', letterSpacing: '-1px' }}>
          Free Coffee Fridays<br />at KGOB <span style={{ whiteSpace: 'nowrap' }}>☕</span>
        </h1>
        <div className="kg-ribbon" style={{ display: 'inline-block', position: 'relative', background: '#1AA0B8', color: '#fff', fontFamily: "'Kaushan Script', cursive", fontSize: 'clamp(20px,3vw,30px)', padding: '12px 46px', borderRadius: '40px 30px 44px 28px', boxShadow: '0 8px 22px rgba(26,160,184,.32)', transform: 'rotate(-1deg)' }}>
          <span style={{ opacity: 0.8 }}>❮ </span>We’re glad you’re here.<span style={{ opacity: 0.8 }}> ❯</span>
        </div>
        <p style={{ maxWidth: 560, margin: '26px auto 0', fontSize: 16.5, lineHeight: 1.6, color: '#46586B' }}>
          Take 10 seconds to fill out the quick form below, then show your confirmation screen to our team to claim your free cup of coffee.
        </p>
        <div style={{ marginTop: 18, fontSize: 15.5, minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16 }}>☕</span>
          <span key={qi} style={{ color: '#2C7E8F', fontStyle: 'italic', fontWeight: 500, animation: animated ? 'kg-fade .6s ease both' : 'none' }}>“{QUIPS[qi]}”</span>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const card = { flex: '1 1 300px', display: 'flex', alignItems: 'center', gap: 16, background: '#FFF', border: '1px solid #ECE3CF', borderRadius: 18, padding: '18px 22px', boxShadow: '0 6px 18px rgba(27,58,92,.06)' };
  const badge = { width: 52, height: 52, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  return (
    <section style={{ maxWidth: 880, margin: '6px auto 0', padding: '24px 24px 0', display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
      <div style={card}>
        <div style={{ ...badge, background: '#1AA0B8' }}>
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 10h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" /><path d="M17 11h3a3 3 0 0 1 0 6h-3" /><path d="M8 5c0-1 .8-1.5.8-2.5" /><path d="M12 5c0-1 .8-1.5.8-2.5" />
          </svg>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1AA0B8', letterSpacing: '.4px' }}>STEP 1</div>
          <div style={{ fontSize: 15, color: '#2B3B4D', lineHeight: 1.4 }}>Fill out the quick form to claim your <strong style={{ color: '#1B3A5C' }}>FREE</strong> coffee.</div>
        </div>
      </div>
      <div style={card}>
        <div style={{ ...badge, background: '#1B3A5C' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="2" width="12" height="20" rx="3" /><path d="M9.5 13l2 2 3.5-3.5" />
          </svg>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#C9A23F', letterSpacing: '.4px' }}>STEP 2</div>
          <div style={{ fontSize: 15, color: '#2B3B4D', lineHeight: 1.4 }}>Show your confirmation screen to a <strong style={{ color: '#1B3A5C' }}>KGOB</strong> team member.</div>
        </div>
      </div>
    </section>
  );
}

function ClaimForm({ onClaim }) {
  const [fields, setFields] = useState({ firstName: '', lastName: '', email: '', phone: '', location: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);

  const set = (k) => (e) => {
    setFields((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: '' }));
    if (k === 'email') { setCodeSent(false); setCode(''); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startResendTimer = () => {
    clearInterval(timerRef.current);
    setResendIn(45);
    timerRef.current = setInterval(() => setResendIn((s) => {
      if (s <= 1) { clearInterval(timerRef.current); return 0; }
      return s - 1;
    }), 1000);
  };

  const sendCode = async () => {
    const response = await fetch(`${backendUrl}/api/v1/coffee/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: fields.email.trim() }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Could not send code');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!fields.firstName.trim()) er.firstName = 'Please enter your first name';
    if (!fields.lastName.trim()) er.lastName = 'Please enter your last name';
    if (!EMAIL_RE.test(fields.email.trim())) er.email = 'Enter a valid email address';
    if (!fields.location) er.location = 'Please pick a location';
    if (Object.keys(er).length) { setErrors(er); return; }

    // Step 1: send the code (first click)
    if (!codeSent) {
      setSubmitting(true);
      try {
        await sendCode();
        setCodeSent(true);
        startResendTimer();
        toast.success('We emailed you a 6-digit code. Enter it below.');
      } catch (err) {
        toast.error(err.message || 'Could not send code. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Step 2: verify the code, then claim
    if (code.trim().length !== 6) {
      setErrors((old) => ({ ...old, code: 'Enter the 6-digit code from your email' }));
      return;
    }
    setSubmitting(true);
    try {
      const vRes = await fetch(`${backendUrl}/api/v1/coffee/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fields.email.trim(), code: code.trim() }),
      });
      const vData = await vRes.json();
      if (!vRes.ok || !vData.verified) {
        setErrors((old) => ({ ...old, code: vData.message || 'Invalid or expired code. Please try again.' }));
        setSubmitting(false);
        return;
      }
      const response = await fetch(`${backendUrl}/api/v1/coffee/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: fields.firstName.trim(),
          last_name: fields.lastName.trim(),
          email: fields.email.trim(),
          phone: fields.phone.trim(),
          location: fields.location,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onClaim({ firstName: fields.firstName.trim(), email: fields.email.trim(), ticket: String(4000 + Math.floor(Math.random() * 1900)) });
        toast.success('Coffee claimed!');
      } else {
        toast.error(data.detail || 'Submission failed. Please try again.');
      }
    } catch (_) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await sendCode();
      startResendTimer();
      setCode('');
      toast.success('New code sent.');
    } catch (err) {
      toast.error(err.message || 'Could not resend code.');
    }
  };

  return (
    <form onSubmit={submit} noValidate className="kg-card" data-testid="coffee-claim-form" style={{ background: '#fff', borderRadius: 24, padding: '38px 34px 34px', boxShadow: '0 22px 60px rgba(27,58,92,.13)', border: '1px solid #EFE7D4' }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
        <label style={{ flex: '1 1 200px', display: 'block' }}>
          <span style={labelS}>First Name <span style={{ color: '#C9A23F' }}>*</span></span>
          <input type="text" value={fields.firstName} onChange={set('firstName')} placeholder="John" style={inputS} data-testid="coffee-first-name" />
          {errors.firstName && <span style={errS}>{errors.firstName}</span>}
        </label>
        <label style={{ flex: '1 1 200px', display: 'block' }}>
          <span style={labelS}>Last Name <span style={{ color: '#C9A23F' }}>*</span></span>
          <input type="text" value={fields.lastName} onChange={set('lastName')} placeholder="Doe" style={inputS} data-testid="coffee-last-name" />
          {errors.lastName && <span style={errS}>{errors.lastName}</span>}
        </label>
      </div>
      <label style={{ display: 'block', marginBottom: 18 }}>
        <span style={labelS}>Email Address <span style={{ color: '#C9A23F' }}>*</span></span>
        <input type="email" value={fields.email} onChange={set('email')} placeholder="john@example.com" style={inputS} data-testid="coffee-email" />
        {errors.email && <span style={errS}>{errors.email}</span>}
      </label>
      {codeSent && (
        <div style={{ marginBottom: 18, background: '#FDF9EE', border: '1px solid #EBDCB2', borderRadius: 13, padding: '16px 18px' }} data-testid="coffee-code-block">
          <span style={labelS}>Enter the 6-digit code we emailed you <span style={{ color: '#C9A23F' }}>*</span></span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setErrors((er) => ({ ...er, code: '' })); }}
            placeholder="123456"
            style={{ ...inputS, textAlign: 'center', fontSize: 22, letterSpacing: 10, fontWeight: 700 }}
            data-testid="coffee-code-input"
          />
          {errors.code && <span style={errS}>{errors.code}</span>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#7C8A98' }}>Sent to {fields.email.trim()} · valid 15 minutes</span>
            <button type="button" onClick={resend} disabled={resendIn > 0} style={{ background: 'none', border: 'none', color: resendIn > 0 ? '#9AA7B2' : '#C9A23F', fontSize: 12.5, cursor: resendIn > 0 ? 'default' : 'pointer', fontFamily: 'inherit', fontWeight: 700 }} data-testid="coffee-code-resend">
              {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
            </button>
          </div>
        </div>
      )}
      <label style={{ display: 'block', marginBottom: 18 }}>
        <span style={labelS}>Phone Number <span style={{ color: '#9AA7B2', fontWeight: 500 }}>(optional)</span></span>
        <input type="tel" value={fields.phone} onChange={set('phone')} placeholder="(704) 555-0123" style={inputS} data-testid="coffee-phone" />
      </label>
      <label style={{ display: 'block', marginBottom: 26 }}>
        <span style={labelS}>Location <span style={{ color: '#C9A23F' }}>*</span></span>
        <select
          value={fields.location} onChange={set('location')} data-testid="coffee-location"
          style={{ ...inputS, appearance: 'none', cursor: 'pointer',
            backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2214%22 height=%2214%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%231B3A5C%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')",
            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 15px center' }}
        >
          <option value="">Select location</option>
          {LOCATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {errors.location && <span style={errS}>{errors.location}</span>}
      </label>
      <button type="submit" disabled={submitting} className="kg-cta" data-testid="coffee-submit" style={{ width: '100%', padding: 16, border: 'none', borderRadius: 13, background: 'linear-gradient(180deg,#E3C66A,#C9A23F)', color: '#1B3A5C', fontWeight: 800, fontSize: 17, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 8px 20px rgba(201,162,63,.4)', letterSpacing: '.2px' }}>
        {submitting ? (codeSent ? 'Verifying…' : 'Sending code…') : (codeSent ? 'Verify & Claim My Coffee ☕' : 'Claim My Free Coffee ☕')}
      </button>
      <p style={{ textAlign: 'center', fontSize: 13, color: '#7C8A98', margin: '16px 0 0', lineHeight: 1.5 }}>
        {codeSent
          ? 'Type the code from your email above, then show your confirmation screen to a KGOB team member to receive your drink.'
          : "We will email you a quick 6-digit code to verify it's really you, then show your confirmation screen to a KGOB team member to receive your drink."}
      </p>
    </form>
  );
}

function Confetti() {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: 24 }}>
      {Array.from({ length: 22 }).map((_, i) => {
        const w = 7 + (i % 3) * 3;
        const h = 10 + (i % 3) * 2;
        return (
          <span key={i} style={{
            position: 'absolute', top: -12, left: `${(i * 4.6 + 3) % 100}%`,
            width: w, height: h, borderRadius: 2, background: CONFETTI_COLORS[i % 4],
            animation: `kg-confetti ${(1.5 + (i % 5) * 0.22).toFixed(2)}s ${((i % 7) * 0.13).toFixed(2)}s ease-in forwards`,
          }} />
        );
      })}
    </div>
  );
}

function SuccessCard({ name, email, ticket, animated, onReset }) {
  return (
    <div className="kg-card" data-testid="coffee-success" style={{ background: '#fff', borderRadius: 24, padding: '48px 36px', textAlign: 'center', boxShadow: '0 22px 60px rgba(27,58,92,.14)', border: '1px solid #EFE7D4', position: 'relative', overflow: 'hidden' }}>
      {animated && <Confetti />}
      <SuccessMug />
      <div style={{ fontFamily: "'Kaushan Script', cursive", color: '#1AA0B8', fontSize: 26, marginBottom: 2 }}>You’re all set,</div>
      <h2 style={{ fontWeight: 800, fontSize: 30, color: '#1B3A5C', margin: '0 0 10px', letterSpacing: '-.5px' }}>{name}! ☕</h2>
      <p style={{ fontSize: 16, color: '#46586B', lineHeight: 1.55, maxWidth: 420, margin: '0 auto 14px' }}>
        Your free coffee is ready. Just show <strong style={{ color: '#1B3A5C' }}>this screen</strong> to a KGOB team member at the counter and they’ll pour you a fresh cup.
      </p>
      <p style={{ fontSize: 13.5, color: '#7C8A98', maxWidth: 420, margin: '0 auto 22px' }}>
        We’ve also emailed a thank-you to <strong style={{ color: '#1B3A5C' }}>{email}</strong> with our contact info.
      </p>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#F7F0E1', border: '1px dashed #C9A23F', borderRadius: 12, padding: '12px 20px', marginBottom: 24 }}>
        <span style={{ fontSize: 13, color: '#8A6A1E', fontWeight: 600, letterSpacing: '.5px' }}>CONFIRMED</span>
        <span style={{ fontWeight: 700, color: '#1B3A5C', fontSize: 15, letterSpacing: '1px' }} data-testid="coffee-ticket">KGOB-FCF-{ticket}</span>
      </div>
      <div>
        <button type="button" onClick={onReset} data-testid="coffee-reset" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1AA0B8', fontWeight: 600, fontSize: 14, borderBottom: '1.5px solid #1AA0B8', padding: '0 0 1px' }}>
          ← Claim another coffee
        </button>
      </div>
    </div>
  );
}

function BrewsForGrowth() {
  return (
    <section className="kg-brews" style={{ position: 'relative', overflow: 'hidden', background: '#FBF6EC', borderTop: '1px solid #EFE6D2', borderBottom: '1px solid #EFE6D2', margin: '14px 0 0', padding: '46px 24px 54px', textAlign: 'center' }}>
      <span aria-hidden="true" style={{ position: 'absolute', left: '6%', top: '24%', animation: 'kg-twinkle 3s ease-in-out infinite' }}><Sparkle size={16} color="#C9A23F" /></span>
      <span aria-hidden="true" style={{ '--r': '12deg', position: 'absolute', right: '7%', top: '30%', animation: 'kg-bean 6s ease-in-out infinite' }}><Bean w={26} /></span>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ fontFamily: "'Kaushan Script', cursive", color: '#1AA0B8', fontSize: 30, lineHeight: 1 }}>Pick your brew</div>
        <h2 style={{ fontWeight: 800, fontSize: 'clamp(26px,3.6vw,38px)', color: '#1B3A5C', margin: '4px 0 10px', letterSpacing: '-.6px' }}>A blend for every stage of growth</h2>
        <p style={{ maxWidth: 560, margin: '0 auto 32px', fontSize: 15.5, color: '#46586B', lineHeight: 1.55 }}>
          However you take it today, here’s the KGOB pairing for wherever your business is headed — coffee in one hand, growth plan in the other.
        </p>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          {BREWS.map((b) => (
            <div key={b.stage} className="kg-brew" style={{ flex: '1 1 210px', maxWidth: 248, background: '#fff', border: '1px solid #ECE3CF', borderRadius: 20, padding: '26px 22px', boxShadow: '0 8px 22px rgba(27,58,92,.07)' }}>
              <div style={{ height: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: 13 }}><BrewCup type={b.art} /></div>
              <div style={{ display: 'inline-block', background: b.badge.bg, color: b.badge.fg, fontWeight: 700, fontSize: 11, letterSpacing: '1.2px', padding: '4px 12px', borderRadius: 20, marginBottom: 11 }}>{b.stage}</div>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#1B3A5C', marginBottom: 7 }}>{b.name}</div>
              <div style={{ fontSize: 13.5, color: '#5A6B7A', lineHeight: 1.5, marginBottom: 15 }}>{b.blurb}</div>
              <div style={{ borderTop: '1px dashed #E2D9C5', paddingTop: 12, fontSize: 12.5, color: '#147F95', fontWeight: 600 }}>Paired with: {b.paired}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 34, display: 'inline-flex', alignItems: 'center', gap: 14, background: '#1B3A5C', borderRadius: 16, padding: '16px 26px', textAlign: 'left', maxWidth: 560, boxShadow: '0 10px 26px rgba(27,58,92,.18)' }}>
          <span style={{ fontSize: 26, flex: 'none' }}>☕</span>
          <span style={{ color: '#D7E2EC', fontSize: 14.5, lineHeight: 1.5 }}>
            Sipping with a founder today? Ask any KGOB team member about <strong style={{ color: '#E3C66A' }}>Let’s Talk Growth®</strong> — a no-pressure chat about your numbers, over a refill.
          </span>
        </div>
      </div>
    </section>
  );
}

const Coffee = () => {
  const [animated, setAnimated] = useState(true);
  const [claim, setClaim] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      setAnimated(!window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
  }, []);

  return (
    <div className="kg-coffee" data-anim={animated ? 'on' : 'off'} data-testid="coffee-page" style={{ background: '#F7F0E1', color: '#26323F', minHeight: '100vh', fontFamily: "'Poppins', system-ui, -apple-system, sans-serif" }}>
      <Helmet>
        <title>Free Coffee Fridays at KGOB ☕ | KGOB CPAs &amp; Advisors</title>
        <meta name="description" content="Join us for Free Coffee Fridays at KGOB. Fill out the quick form, show your confirmation, and claim a free cup on us." />
        <link rel="canonical" href="https://kgob.com/coffee" />
        <link rel="stylesheet" href="/coffee-fonts/fonts.css" />
      </Helmet>

      <Header />
      <Hero animated={animated} />
      <Steps />

      <section className="kg-form-section" style={{ maxWidth: 680, margin: '34px auto 0', padding: '0 24px 70px', position: 'relative' }}>
        <div className="kg-form-mug" style={{ position: 'absolute', left: -14, top: -58, zIndex: 2, animation: 'kg-sip 5.2s ease-in-out infinite', transformOrigin: 'bottom center' }}>
          <TealMug size={96} showSteam />
        </div>
        {claim
          ? <SuccessCard name={claim.firstName} email={claim.email} ticket={claim.ticket} animated={animated} onReset={() => setClaim(null)} />
          : <ClaimForm onClaim={setClaim} />}
      </section>

      <BrewsForGrowth />
      <Footer />
    </div>
  );
};

export default Coffee;
